import { useCallback, useSyncExternalStore } from "react";
import { logFCI } from "@/shared/lib/fci";

export type UIComponentName =
    | "accordion"
    | "alert"
    | "autocomplete"
    | "autogrow"
    | "callout"
    | "carousel"
    | "modal"
    | "paging"
    | "sidebar"
    | "switch"
    | "tabs"
    | "validator";

const DEFAULT_POLL_INTERVAL = 250;
const DEFAULT_MAX_RETRIES = 20;

const isUIComponentAvailable = (componentName: UIComponentName): boolean =>
    typeof window !== "undefined" && !!(window as unknown as { ui?: Record<string, unknown> }).ui?.[componentName];

interface UseUIComponentLoadedOptions {
    /** How often to poll for the component, in milliseconds. */
    pollInterval?: number;
    /** Maximum polling attempts before giving up and logging a Timeout FCI. */
    maxRetries?: number;
}

/**
 * Per-component-name readiness state, shared by every subscriber of that name.
 *
 * A single poller runs per name regardless of how many components subscribe, and a
 * single `Timeout` FCI is logged per name on retry-budget exhaustion — never one per
 * subscriber.
 */
interface ReadinessEntry {
    ready: boolean;
    listeners: Set<() => void>;
    intervalId: ReturnType<typeof setInterval> | null;
    retryCount: number;
    /** Ensures at most one Timeout FCI is logged per component name. */
    fciLogged: boolean;
}

const store = new Map<UIComponentName, ReadinessEntry>();

const getEntry = (componentName: UIComponentName): ReadinessEntry => {
    let entry = store.get(componentName);

    if (!entry) {
        entry = { ready: false, listeners: new Set(), intervalId: null, retryCount: 0, fciLogged: false };
        store.set(componentName, entry);
    }

    return entry;
};

const notify = (entry: ReadinessEntry): void => {
    entry.listeners.forEach((listener) => listener());
};

const stopPoller = (entry: ReadinessEntry): void => {
    if (entry.intervalId !== null) {
        clearInterval(entry.intervalId);
        entry.intervalId = null;
    }
};

const markReady = (entry: ReadinessEntry): void => {
    stopPoller(entry);

    if (!entry.ready) {
        entry.ready = true;
        notify(entry);
    }
};

const startPoller = (componentName: UIComponentName, pollInterval: number, maxRetries: number): void => {
    const entry = getEntry(componentName);

    // A single poller per name: already ready, already polling, or already timed out.
    if (entry.ready || entry.intervalId !== null || entry.fciLogged) return;

    if (isUIComponentAvailable(componentName)) {
        markReady(entry);
        return;
    }

    entry.retryCount = 0;
    entry.intervalId = setInterval(() => {
        entry.retryCount += 1;

        if (isUIComponentAvailable(componentName)) {
            markReady(entry);
        } else if (entry.retryCount >= maxRetries) {
            stopPoller(entry);

            if (!entry.fciLogged) {
                entry.fciLogged = true;
                logFCI({
                    errorType: "Timeout",
                    errorInfo: `window.ui.${componentName} unavailable after ${maxRetries} attempts`,
                    details: { component: componentName },
                });
            }
        }
    }, pollInterval);
};

const subscribeToUIComponent = (
    componentName: UIComponentName,
    listener: () => void,
    pollInterval: number,
    maxRetries: number,
): (() => void) => {
    const entry = getEntry(componentName);
    entry.listeners.add(listener);

    startPoller(componentName, pollInterval, maxRetries);

    return () => {
        entry.listeners.delete(listener);

        // Once no component is subscribed, stop the shared poller. `ready`/`fciLogged`
        // persist so a later remount neither re-polls a resolved name nor re-logs.
        if (entry.listeners.size === 0) {
            stopPoller(entry);
        }
    };
};

const getSnapshot = (componentName: UIComponentName): boolean => store.get(componentName)?.ready ?? false;

/**
 * Clears the module-global readiness store. Test-only: the store is process-wide
 * module state that must be reset between cases.
 */
export const __resetUIComponentStoreForTests = (): void => {
    store.forEach((entry) => stopPoller(entry));
    store.clear();
};

/**
 * Hydration-safe readiness signal for Ancestry's vanilla UI library (`window.ui.*`,
 * populated by `core.js`).
 *
 * This hook returns `false` on the server **and** on the first client render, then
 * flips to `true` in a subscription. This is mandatory when the flag gates rendered
 * markup: reading `window.ui` during render makes the server (no `window`) and client
 * disagree, producing a hydration mismatch. The initial state is therefore always
 * `false` and we never read `window.ui` during render.
 *
 * Readiness is backed by a module-level store keyed by component name. All components
 * subscribing to the same `window.ui.<name>` share a single poller, and a single
 * `Timeout` FCI is logged per name on retry-budget exhaustion — not one per subscriber.
 *
 * On CMS pages `core.js` is loaded with `defer`, so it may not have populated
 * `window.ui` by the time React hydrates. The shared poller checks every `pollInterval`
 * ms up to `maxRetries` attempts. If the component never appears within the retry
 * budget we log a `Timeout` FCI so a genuine "core.js never loaded" case is visible in
 * telemetry.
 */
export const useUIComponentLoaded = (
    componentName: UIComponentName,
    { pollInterval = DEFAULT_POLL_INTERVAL, maxRetries = DEFAULT_MAX_RETRIES }: UseUIComponentLoadedOptions = {},
): boolean => {
    const subscribe = useCallback(
        (listener: () => void) => subscribeToUIComponent(componentName, listener, pollInterval, maxRetries),
        [componentName, pollInterval, maxRetries],
    );

    return useSyncExternalStore(
        subscribe,
        () => getSnapshot(componentName),
        // Hydration-safe: never read window.ui during render (server or first client render).
        () => false,
    );
};
